import { useEffect, useState } from 'react';
import { audioContext } from '@/audio/context';
import { cx } from '@/lib/cx';
import s from './debug.module.css';
import { Card } from './ui';
import { copyText, fmtBytes, useToast } from './util';

type Info = [label: string, value: string][];

const media = (q: string) => (window.matchMedia(q).matches ? 'yes' : 'no');

function screenInfo(): Info {
  return [
    ['Viewport', `${String(window.innerWidth)} × ${String(window.innerHeight)} CSS px`],
    [
      'Visual viewport',
      `${String(Math.round(window.visualViewport?.width ?? 0))} × ${String(Math.round(window.visualViewport?.height ?? 0))}`,
    ],
    ['Screen', `${String(screen.width)} × ${String(screen.height)}`],
    ['Pixel ratio', String(window.devicePixelRatio)],
    [
      'Orientation',
      // Missing before iOS 16.4.
      (screen as Partial<Screen>).orientation?.type ??
        (media('(orientation: portrait)') === 'yes' ? 'portrait' : 'landscape'),
    ],
    ['Touch (coarse pointer)', media('(pointer: coarse)')],
    ['Reduced motion', media('(prefers-reduced-motion: reduce)')],
    ['Dark scheme', media('(prefers-color-scheme: dark)')],
    ['Standalone (home screen)', media('(display-mode: standalone)')],
  ];
}

function browserInfo(): Info {
  return [
    ['User agent', navigator.userAgent],
    ['Language', navigator.language],
    ['Online', navigator.onLine ? 'yes' : 'no'],
    ['Secure context', window.isSecureContext ? 'yes' : 'no'],
    ['URL', window.location.href],
  ];
}

async function audioInfo(): Promise<Info> {
  const out: Info = [];
  try {
    const c = audioContext();
    out.push(['AudioContext', `${c.state}, ${String(c.sampleRate)} Hz`]);
    out.push(['Base latency', `${(c.baseLatency * 1000).toFixed(1)} ms`]);
    if ('outputLatency' in c)
      out.push(['Output latency', `${(c.outputLatency * 1000).toFixed(1)} ms`]);
    out.push(['AudioWorklet', 'audioWorklet' in c ? 'yes' : 'no']);
  } catch (e) {
    out.push(['AudioContext', `unavailable: ${String(e)}`]);
  }
  const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
  out.push(['Audio session', session ? session.type : 'not supported']);
  out.push(['getUserMedia', 'mediaDevices' in navigator ? 'yes' : 'no (needs HTTPS)']);
  try {
    const p = await navigator.permissions.query({ name: 'microphone' });
    out.push(['Microphone permission', p.state]);
  } catch {
    out.push(['Microphone permission', 'unknown (no Permissions API for it)']);
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const count = (kind: MediaDeviceKind) => devices.filter((d) => d.kind === kind).length;
    out.push([
      'Devices',
      `${String(count('audioinput'))} inputs, ${String(count('audiooutput'))} outputs`,
    ]);
  } catch {
    // not available
  }
  out.push([
    'Speech synthesis',
    'speechSynthesis' in window
      ? `${String(window.speechSynthesis.getVoices().length)} voices`
      : 'no',
  ]);
  return out;
}

async function storageInfo(): Promise<Info> {
  const out: Info = [];
  try {
    const e = await navigator.storage.estimate();
    out.push(['Used / quota', `${fmtBytes(e.usage ?? 0)} / ${fmtBytes(e.quota ?? 0)}`]);
    out.push(['Persistent', (await navigator.storage.persisted()) ? 'yes' : 'no']);
  } catch {
    out.push(['Storage estimate', 'not available']);
  }
  out.push(['IndexedDB', typeof indexedDB === 'undefined' ? 'no' : 'yes']);
  return out;
}

const BUILD: Info = [
  ['Version', __APP_VERSION__],
  ['Built', __BUILD_TIME__],
  ['Mode', import.meta.env.MODE],
];

/** The device, browser, audio and build, for bug reports: everything in one copyable text. */
export function DevicePanel() {
  const toast = useToast();
  const [screenRows, setScreenRows] = useState(screenInfo);
  const [audio, setAudio] = useState<Info>([]);
  const [storage, setStorage] = useState<Info>([]);
  const refresh = () => {
    setScreenRows(screenInfo());
    void audioInfo().then(setAudio);
    void storageInfo().then(setStorage);
  };
  useEffect(() => {
    void audioInfo().then(setAudio);
    void storageInfo().then(setStorage);
    const onResize = () => {
      setScreenRows(screenInfo());
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const sections: [string, Info][] = [
    ['Build', BUILD],
    ['Screen', screenRows],
    ['Audio', audio],
    ['Browser', browserInfo()],
    ['Storage', storage],
  ];
  const report = () =>
    sections
      .map(([title, rows]) => `## ${title}\n${rows.map(([k, v]) => `${k}: ${v}`).join('\n')}`)
      .join('\n\n');

  return (
    <>
      <div className={s.row}>
        <button
          type="button"
          className={cx(s.btn, s.primary)}
          onClick={() => {
            void copyText(report()).then((ok) => {
              toast(ok ? 'Copied the device report' : 'Copy failed');
            });
          }}
        >
          Copy report
        </button>
        <button type="button" className={s.btn} onClick={refresh}>
          Refresh
        </button>
      </div>
      <div className={s.grid2}>
        {sections.map(([title, rows]) => (
          <Card key={title} title={title}>
            <dl className={s.kv}>
              {rows.map(([k, v]) => (
                <Row key={k} label={k} value={v} />
              ))}
            </dl>
          </Card>
        ))}
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}
